/**
 * The two-body half of the readiness readings (GDS §12 "Cooperative frame width", Slice A′).
 *
 * Same boundary rule as `frame-readings.ts`, for the same reason: landmarks come in, **numbers and
 * settled booleans go out**, so the shell can render a two-hero checklist without ever being able to
 * see a child's body (architecture §11, §25). Nothing here is persisted or transmitted.
 *
 * Two pieces, and the split is deliberate:
 *
 *  - {@link cooperativeObservationFrom} measures one frame. It knows nothing about thresholds or
 *    history, so it is a measurement and never a verdict.
 *  - {@link CooperativeReadingsGate} is the one thing that remembers. The Slice 0′ spike measured a
 *    **5–7 frame** cost to acquire a second body (D-077(2)(d)) and found that a body half out of
 *    frame can produce two readings of one child (D-077(2)(b)); a row that judged each frame on its
 *    own would flicker between "needs more width" and "ready" while a family stood still. The gate
 *    publishes a verdict only once it has held for the confirmation window — and it is the **only**
 *    thing that knows whether two bodies are in the picture, so nothing downstream re-derives it
 *    (B.2's one-owner rule, D-075(1)).
 */

import {
  CAMERA_READINESS_THRESHOLDS,
  type CameraReadinessThresholds,
} from '../../platform/config/camera-readiness';
import { CORE_POSE_LANDMARKS, type PoseFrame, type PoseLandmarkPoint } from '../pose';
import type { CooperativeReadings } from './readiness';

/** One frame's worth of two-body measurement. Numbers only; no verdict. */
export interface CooperativeObservation {
  /** How many bodies this frame reported. `numPoses` bounds it, so 0, 1 or 2 in practice. */
  readonly bodyCount: number;
  /**
   * Distance between the two bodies' centres, as a fraction of frame width. Null with fewer than
   * two bodies — which is a state, not a zero.
   */
  readonly centreSeparation: number | null;
}

/** Mean horizontal position of a body's confidently visible core landmarks, or null. */
function centreXOf(landmarks: readonly PoseLandmarkPoint[], minConfidence: number): number | null {
  const core = CORE_POSE_LANDMARKS.map((index) => landmarks[index]).filter(
    (point): point is PoseLandmarkPoint => point !== undefined,
  );
  const visible = core.filter((point) => point.visibility >= minConfidence);
  // The same fallback `bodyReadingsFrom` makes: "we can see something, badly" must stay
  // distinguishable from "nobody is there".
  const measured = visible.length >= 2 ? visible : core;
  if (measured.length === 0) return null;

  return measured.reduce((total, point) => total + point.x, 0) / measured.length;
}

/**
 * Measures how many bodies a frame holds and how far apart they are.
 *
 * Ordered by confidence rather than by `poseIndex`, because the spike established that the index is
 * a **track slot and never an identity** — it renumbers after a loss (D-077(2)(a)) — and a
 * measurement that moved when the slots renumbered would make the width row change for a reason no
 * family could see. The gap is an absolute distance, so it is the same however the two are ordered.
 */
export function cooperativeObservationFrom(
  frame: PoseFrame,
  minConfidence: number,
): CooperativeObservation {
  const centres = [...frame.poses]
    .sort((a, b) => b.coreConfidence - a.coreConfidence)
    .map((pose) => centreXOf(pose.landmarks, minConfidence))
    .filter((centre): centre is number => centre !== null);

  const [first, second] = centres;
  if (first === undefined || second === undefined) {
    return { bodyCount: centres.length, centreSeparation: null };
  }

  return { bodyCount: centres.length, centreSeparation: Math.abs(first - second) };
}

/** What the gate has settled on, before any threshold is re-read. */
interface Verdict {
  readonly twoBodies: boolean;
  readonly enoughWidth: boolean | null;
}

function verdictOf(
  observation: CooperativeObservation,
  thresholds: CameraReadinessThresholds,
): Verdict {
  const separation = observation.centreSeparation;
  if (observation.bodyCount < 2 || separation === null) {
    return { twoBodies: false, enoughWidth: null };
  }
  return {
    twoBodies: true,
    enoughWidth: separation >= thresholds.cooperative.minCentreSeparation,
  };
}

function sameVerdict(left: Verdict, right: Verdict): boolean {
  return left.twoBodies === right.twoBodies && left.enoughWidth === right.enoughWidth;
}

/**
 * Turns per-frame observations into a settled cooperative reading.
 *
 * Clock-free on purpose, exactly as the tracking-loss monitor is (D-065(1)): it counts frames,
 * because frames are what acquisition costs. A machine that is running slowly therefore waits the
 * same number of *detections*, not the same number of milliseconds.
 */
export class CooperativeReadingsGate {
  private readonly thresholds: CameraReadinessThresholds;
  private settled: Verdict | null = null;
  private candidate: Verdict | null = null;
  private candidateFrames = 0;
  private latestSeparation: number | null = null;

  constructor(thresholds: CameraReadinessThresholds = CAMERA_READINESS_THRESHOLDS) {
    this.thresholds = thresholds;
  }

  /**
   * Feeds one frame's measurement in and returns what the screen may say, or null while nothing has
   * held long enough to be worth saying. Null is silence, never a guess.
   */
  observe(observation: CooperativeObservation): CooperativeReadings | null {
    const verdict = verdictOf(observation, this.thresholds);

    if (this.settled !== null && sameVerdict(verdict, this.settled)) {
      // Agreement with what is already on screen: the numbers refresh, the verdict does not move,
      // and any part-formed change is abandoned.
      this.candidate = null;
      this.candidateFrames = 0;
      if (verdict.twoBodies) this.latestSeparation = observation.centreSeparation;
      return this.published();
    }

    if (this.candidate !== null && sameVerdict(verdict, this.candidate)) {
      this.candidateFrames += 1;
    } else {
      this.candidate = verdict;
      this.candidateFrames = 1;
    }

    if (this.candidateFrames < this.thresholds.cooperative.confirmFrames) {
      return this.published();
    }

    this.settled = verdict;
    this.latestSeparation = verdict.twoBodies ? observation.centreSeparation : null;
    this.candidate = null;
    this.candidateFrames = 0;
    return this.published();
  }

  /** The end of a look at the room: nothing carries over into the next one. */
  reset(): void {
    this.settled = null;
    this.candidate = null;
    this.candidateFrames = 0;
    this.latestSeparation = null;
  }

  private published(): CooperativeReadings | null {
    if (this.settled === null) return null;
    return {
      twoBodies: this.settled.twoBodies,
      enoughWidth: this.settled.enoughWidth,
      centreSeparation: this.settled.twoBodies ? this.latestSeparation : null,
    };
  }
}
